// src/components/UserHistoryModal.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaTimes, FaHistory, FaSignInAlt, FaSignOutAlt, FaLock,
  FaKey, FaUserEdit, FaShieldAlt, FaExclamationTriangle,
  FaExternalLinkAlt, FaVideo, FaChartLine, FaMousePointer,
  FaDesktop, FaClock, FaBolt, FaUserSlash, FaRedoAlt,
  FaCheckCircle, FaSyncAlt, FaWifi, FaUser, FaUserSecret, FaSpinner
} from 'react-icons/fa';

/* ================================================================================================
 * Mapa de ações → visual (cor, label, ícone)
 * ================================================================================================ */
const ACTION_MAP = {
  LOGIN:                    { color: '#10b981', bg: '#d1fae5', label: 'Login realizado',        Icon: FaSignInAlt },
  LOGOUT:                   { color: '#3b82f6', bg: '#dbeafe', label: 'Logout',                 Icon: FaSignOutAlt },
  LOGIN_FALHA:              { color: '#ef4444', bg: '#fee2e2', label: 'Tentativa inválida',     Icon: FaExclamationTriangle },
  LOGOFF_FORCADO:           { color: '#f97316', bg: '#ffedd5', label: 'Logoff forçado',         Icon: FaBolt },
  LOGOFF_MASSA_FORCADO:     { color: '#f97316', bg: '#ffedd5', label: 'Logoff em massa',        Icon: FaBolt },
  USUARIO_SENHA_ALTERADA:   { color: '#f59e0b', bg: '#fef3c7', label: 'Senha alterada',        Icon: FaKey },
  USUARIO_ATUALIZADO:       { color: '#8b5cf6', bg: '#ede9fe', label: 'Dados atualizados',     Icon: FaUserEdit },
  USUARIO_CRIADO:           { color: '#10b981', bg: '#d1fae5', label: 'Usuário criado',         Icon: FaCheckCircle },
  USUARIO_EXCLUIDO:         { color: '#ef4444', bg: '#fee2e2', label: 'Exclusão (soft)',        Icon: FaUserSlash },
  USUARIO_EXCLUSAO_DEFINITIVA:{ color: '#7f1d1d', bg: '#fee2e2', label: 'Exclusão definitiva', Icon: FaUserSlash },
  USUARIO_RESTAURADO:       { color: '#10b981', bg: '#d1fae5', label: 'Restaurado',             Icon: FaSyncAlt },
  USUARIO_STATUS_ALTERADO:  { color: '#0ea5e9', bg: '#e0f2fe', label: 'Status alterado',       Icon: FaShieldAlt },
  MFA_RESET:                { color: '#0ea5e9', bg: '#e0f2fe', label: 'MFA resetado',           Icon: FaRedoAlt },
  FOTO_PERFIL_ATUALIZADA:   { color: '#8b5cf6', bg: '#ede9fe', label: 'Foto atualizada',       Icon: FaUserEdit },
  BLOQUEIO_MASSA:           { color: '#ef4444', bg: '#fee2e2', label: 'Bloqueio em massa',      Icon: FaLock },
  DESBLOQUEIO_MASSA:        { color: '#10b981', bg: '#d1fae5', label: 'Desbloqueio em massa',  Icon: FaLock },
};

const getAction = (acao) =>
  ACTION_MAP[acao] || { color: '#6b7280', bg: '#f3f4f6', label: acao, Icon: FaHistory };

/* ================================================================================================
 * Utils
 * ================================================================================================ */
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
};

const fmtDuration = (seconds) => {
  if (!seconds) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1)  return 'agora';
  if (m < 60) return `${m}min atrás`;
  if (h < 24) return `${h}h atrás`;
  if (d < 30) return `${d}d atrás`;
  return fmtDate(iso);
};

/* ================================================================================================
 * Estilos inline (modal não depende de CSS global além das classes .blb-overlay/.blb-modal)
 * ================================================================================================ */
const S = {
  spinnerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#94a3b8'
  },
  spinAnimation: `
    @keyframes blbSpin { 100% { transform: rotate(360deg); } }
  `,
  modal: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 840,
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: '#0f172a',
    color: '#fff',
    borderRadius: '16px 16px 0 0',
  },
  avatar: (color) => ({
    width: 44, height: 44, borderRadius: '50%',
    background: color,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 18, color: '#fff', flexShrink: 0,
  }),
  tabBar: {
    display: 'flex',
    gap: 0,
    padding: '0 24px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  tab: (active) => ({
    padding: '12px 18px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    color: active ? '#0f172a' : '#64748b',
    borderBottom: active ? '2px solid #f59e0b' : '2px solid transparent',
    display: 'flex', alignItems: 'center', gap: 7,
    transition: 'all .15s',
    userSelect: 'none',
    background: 'none', border: 'none', outline: 'none',
  }),
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: 24,
  },
  /* Timeline */
  timeline: {
    position: 'relative',
    paddingLeft: 28,
  },
  timelineLine: {
    position: 'absolute',
    left: 11,
    top: 0, bottom: 0,
    width: 2,
    background: '#e2e8f0',
  },
  timelineItem: {
    position: 'relative',
    marginBottom: 18,
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
  },
  dot: (color) => ({
    position: 'absolute',
    left: -22,
    top: 6,
    width: 14, height: 14,
    borderRadius: '50%',
    background: color,
    border: '2px solid #fff',
    boxShadow: `0 0 0 2px ${color}33`,
    flexShrink: 0,
  }),
  logCard: {
    flex: 1,
    background: '#f8fafc',
    borderRadius: 10,
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
  },
  logAction: (color) => ({
    fontWeight: 600,
    fontSize: 13,
    color,
    display: 'flex', alignItems: 'center', gap: 6,
  }),
  logDetails: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 3,
    lineHeight: 1.5,
  },
  logMeta: {
    display: 'flex', gap: 12, marginTop: 6,
    fontSize: 11, color: '#94a3b8',
    flexWrap: 'wrap',
  },
  /* PostHog */
  phHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 20,
    padding: '14px 18px',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: 12,
    color: '#fff',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  statCard: (color) => ({
    background: '#f8fafc',
    border: `1px solid ${color}33`,
    borderLeft: `3px solid ${color}`,
    borderRadius: 10,
    padding: '12px 14px',
  }),
  statValue: {
    fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1,
  },
  statLabel: {
    fontSize: 11, color: '#64748b', marginTop: 4,
    textTransform: 'uppercase', letterSpacing: '.5px',
  },
  sessionCard: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '11px 14px',
    background: '#f8fafc',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    marginBottom: 8,
    textDecoration: 'none',
    transition: 'all .15s',
    cursor: 'pointer',
  },
  sessionTime: {
    fontSize: 12, fontWeight: 600, color: '#0f172a', flex: 1,
  },
  sessionMeta: {
    fontSize: 11, color: '#64748b',
  },
  pill: (color, bg) => ({
    display: 'inline-flex', alignItems: 'center',
    gap: 4, fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 99,
    color, background: bg,
  }),
  btnPh: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '9px 16px', borderRadius: 8,
    background: '#f8750033', color: '#f87000',
    border: '1px solid #f8700044',
    fontWeight: 600, fontSize: 13,
    cursor: 'pointer', textDecoration: 'none',
    transition: 'background .15s',
  },
  empty: {
    textAlign: 'center', padding: '40px 0',
    color: '#94a3b8', fontSize: 13,
  },
  setupBox: {
    background: '#fffbeb',
    border: '1px solid #fbbf24',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
};

const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#0ea5e9','#ec4899'];
const avatarColor = (name = '') => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

/* ================================================================================================
 * Componente Principal
 * ================================================================================================ */
export function UserHistoryModal({ usuarioId, onClose, perfilLogado }) {
  const [activeTab, setActiveTab] = useState('auditoria');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const modalRef                  = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/usuarios/${usuarioId}/audit?limit=100`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível carregar o histórico deste usuário.');
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [usuarioId, onClose]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleOverlay = (e) => {
    if (e.target.classList.contains('blb-overlay')) onClose?.();
  };

  const handleImpersonate = async () => {
    if (!data?.user?.nome) return;
    if (!window.confirm(`Entrar no sistema como "${data.user.nome}"?\n\nUma nova aba será aberta. Todas as ações ficam registradas nos logs.`)) return;
    try {
      setImpersonating(true);
      const { data: res } = await api.post(`/api/usuarios/${usuarioId}/impersonate`);
      const url = `${window.location.origin}/dashboard?impersonation_token=${res.token}&impersonated_name=${encodeURIComponent(res.nome)}`;
      window.open(url, '_blank');
      toast.info(`Sessão aberta como ${res.nome}.`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao iniciar impersonação.');
    } finally {
      setImpersonating(false);
    }
  };

  // Valores default seguros durante o carregamento
  const user = data?.user || { nome: 'Carregando...', email: '', status: 'ativo' };
  const logs = data?.logs || [];
  const posthog = data?.posthog || null;
  const showPosthog = perfilLogado === 'admin_geral';

  return (
    <div className="blb-overlay" role="dialog" aria-modal="true" onMouseDown={handleOverlay}>
      <style>{S.spinAnimation}</style>
      <div style={S.modal} ref={modalRef}>

        {/* HEADER */}
        <div style={S.header}>
          <div style={S.avatar(avatarColor(user.nome))}>
            {loading && !data ? <FaSpinner style={{ animation: 'blbSpin 1s linear infinite' }} /> : user.nome?.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              {user.nome}
              {loading && data && <FaSpinner size={12} style={{ animation: 'blbSpin 1s linear infinite', color: '#f87171' }} title="Atualizando..." />}
            </div>
            <div style={{ fontSize: 12, opacity: .7 }}>{user.email}</div>
          </div>
          
          {data && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span style={S.pill(
                user.status === 'ativo' ? '#10b981' : '#ef4444',
                user.status === 'ativo' ? '#d1fae5' : '#fee2e2',
              )}>
                {user.status === 'ativo' ? '● Ativo' : '● Bloqueado'}
              </span>
              <span style={{ fontSize: 11, opacity: .6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <FaWifi size={10} /> Último acesso: {timeAgo(user.ultimo_acesso)}
              </span>
            </div>
          )}

          {/* Botão impersonação — exclusivo admin_geral */}
          {showPosthog && data && (
            <button
              onClick={handleImpersonate}
              disabled={impersonating || user.status === 'bloqueado'}
              title={user.status === 'bloqueado' ? 'Usuário bloqueado' : `Entrar como ${user.nome}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '7px 14px', borderRadius: 8,
                background: impersonating ? 'rgba(249,115,22,.15)' : 'rgba(249,115,22,.2)',
                border: '1px solid rgba(249,115,22,.4)',
                color: '#fb923c', cursor: impersonating ? 'wait' : 'pointer',
                fontSize: 12, fontWeight: 700,
                opacity: user.status === 'bloqueado' ? .4 : 1,
                transition: 'background .15s',
                flexShrink: 0,
                marginLeft: 12
              }}
              onMouseEnter={e => { if (!impersonating) e.currentTarget.style.background = 'rgba(249,115,22,.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(249,115,22,.2)'; }}
            >
              {impersonating ? <FaSpinner style={{ animation: 'blbSpin 1s linear infinite' }} /> : <FaUserSecret size={12} />}
              {impersonating ? 'Abrindo…' : 'Entrar como'}
            </button>
          )}

          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 6, marginLeft: 8 }}
            aria-label="Fechar"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* TABS */}
        <div style={S.tabBar}>
          <button style={S.tab(activeTab === 'auditoria')} onClick={() => setActiveTab('auditoria')}>
            <FaHistory size={13} /> Auditoria
            {logs.length > 0 && (
              <span style={{ background: '#0f172a', color: '#fff', borderRadius: 99, padding: '1px 6px', fontSize: 10 }}>
                {logs.length}
              </span>
            )}
          </button>
          {showPosthog && (
            <button style={S.tab(activeTab === 'posthog')} onClick={() => setActiveTab('posthog')}>
              <FaChartLine size={13} /> PostHog
              {posthog?.found && (
                <span style={S.pill('#f87000', '#fff7ed')}>live</span>
              )}
            </button>
          )}
        </div>

        {/* BODY */}
        <div style={S.body}>
          {loading && !data ? (
            <div style={S.spinnerContainer}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <FaSpinner size={32} style={{ animation: 'blbSpin 1s linear infinite', color: '#3b82f6' }} />
                <span>Buscando histórico...</span>
              </div>
            </div>
          ) : (
            <>
              {/* ── ABA AUDITORIA ── */}
              {activeTab === 'auditoria' && (
                <>
                  {logs.length === 0 ? (
                    <div style={S.empty}>
                      <FaHistory size={28} style={{ opacity: .3, marginBottom: 10 }} />
                      <div>Nenhum registro de atividade encontrado para este usuário.</div>
                    </div>
                  ) : (
                    <div style={S.timeline}>
                      <div style={S.timelineLine} />
                      {logs.map((log) => {
                        const { color, label, Icon } = getAction(log.acao);
                        return (
                          <div key={log.id} style={S.timelineItem}>
                            <span style={S.dot(color)} />
                            <div style={S.logCard}>
                              <div style={S.logAction(color)}>
                                <Icon size={12} />
                                {label}
                              </div>
                              {log.detalhes && (
                                <div style={S.logDetails}>{log.detalhes}</div>
                              )}
                              <div style={S.logMeta}>
                                <span><FaClock size={9} /> {fmtDate(log.created_at)}</span>
                                {log.ip && <span>IP: {log.ip}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* ── ABA POSTHOG ── */}
              {activeTab === 'posthog' && showPosthog && (
                <PosthogPanel posthog={posthog} user={user} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================================================
 * Painel PostHog
 * ================================================================================================ */
function PosthogPanel({ posthog, user }) {
  // PostHog não configurado no backend
  if (!posthog) {
    return (
      <div style={S.setupBox}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 8 }}>
          PostHog não configurado
        </div>
        <p style={{ fontSize: 13, color: '#78350f', margin: '0 0 12px' }}>
          Para ver gravações de sessão e dados de uso, adicione as variáveis ao <code>.env</code> do backend:
        </p>
        <pre style={{
          background: '#1e293b', color: '#e2e8f0',
          borderRadius: 8, padding: '12px 16px',
          fontSize: 12, overflowX: 'auto', margin: 0,
        }}>
{`POSTHOG_PERSONAL_API_KEY=phx_SuaChaveAqui
POSTHOG_PROJECT_ID=12345
POSTHOG_HOST=https://us.posthog.com  # ou eu.posthog.com`}
        </pre>
        <p style={{ fontSize: 12, color: '#92400e', marginTop: 10, marginBottom: 0 }}>
          Gere sua chave pessoal em:{' '}
          <a href="https://us.posthog.com/settings/user-api-keys" target="_blank" rel="noreferrer"
             style={{ color: '#d97706' }}>
            posthog.com → Settings → Personal API Keys
          </a>
        </p>
      </div>
    );
  }

  // Usuário não encontrado no PostHog
  if (!posthog.found) {
    return (
      <div style={{ ...S.empty, padding: '30px 0' }}>
        <FaDesktop size={28} style={{ opacity: .3, marginBottom: 10 }} />
        <div style={{ fontWeight: 600, color: '#475569' }}>Usuário não encontrado no PostHog</div>
        <div style={{ fontSize: 12, marginTop: 6 }}>
          {posthog.error || `O e-mail ${user.email} ainda não gerou eventos rastreados.`}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header PostHog */}
      <div style={S.phHeader}>
        <FaChartLine size={20} style={{ color: '#f87000' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Análise de Comportamento</div>
          <div style={{ fontSize: 12, opacity: .7 }}>
            Dados via PostHog · Distinct ID: <code style={{ opacity: .9 }}>{posthog.distinctId}</code>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a href={posthog.profileUrl} target="_blank" rel="noreferrer" style={S.btnPh}>
            <FaUser size={11} /> Ver perfil
          </a>
          <a href={posthog.recordingsUrl} target="_blank" rel="noreferrer"
             style={{ ...S.btnPh, background: '#3b82f633', color: '#3b82f6', borderColor: '#3b82f644' }}>
            <FaVideo size={11} /> Todas as gravações
          </a>
        </div>
      </div>

      {/* Cards de métricas */}
      <div style={S.statsGrid}>
        <div style={S.statCard('#10b981')}>
          <div style={S.statValue}>{posthog.eventCount ?? '—'}</div>
          <div style={S.statLabel}>Eventos totais</div>
        </div>
        <div style={S.statCard('#3b82f6')}>
          <div style={S.statValue}>{posthog.sessionCount ?? posthog.sessions?.length ?? '—'}</div>
          <div style={S.statLabel}>Sessões gravadas</div>
        </div>
        <div style={S.statCard('#8b5cf6')}>
          <div style={S.statValue}>{timeAgo(posthog.lastSeen)}</div>
          <div style={S.statLabel}>Última atividade</div>
        </div>
        <div style={S.statCard('#f59e0b')}>
          <div style={S.statValue}>{fmtDate(posthog.firstSeen).split(' ')[0]}</div>
          <div style={S.statLabel}>Primeiro acesso</div>
        </div>
      </div>

      {/* Gravações recentes */}
      {posthog.sessions?.length > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 12 }}>
            <FaVideo size={12} style={{ marginRight: 6, color: '#64748b' }} />
            Gravações recentes
          </div>
          {posthog.sessions.map((s, i) => (
            <a
              key={s.id}
              href={s.url}
              target="_blank"
              rel="noreferrer"
              style={S.sessionCard}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: i === 0 ? '#3b82f6' : '#e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <FaVideo size={13} color={i === 0 ? '#fff' : '#64748b'} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={S.sessionTime}>{fmtDate(s.startTime)}</div>
                <div style={S.sessionMeta}>
                  {fmtDuration(s.duration)} · {s.pageviews} páginas
                  {s.clicks > 0 && ` · ${s.clicks} cliques`}
                  {s.active > 0 && ` · ${fmtDuration(s.active)} ativo`}
                </div>
              </div>
              <FaExternalLinkAlt size={11} style={{ color: '#94a3b8' }} />
            </a>
          ))}
        </>
      )}

      {posthog.sessions?.length === 0 && (
        <div style={{ ...S.empty, paddingTop: 24 }}>
          <FaVideo size={24} style={{ opacity: .3, marginBottom: 8 }} />
          <div>Nenhuma gravação de sessão disponível.</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>
            Verifique se a gravação está habilitada no projeto PostHog.
          </div>
        </div>
      )}
    </>
  );
}

export default UserHistoryModal;